import { CheckIcon, ExclamationMarkIcon } from '@panwds/icons';
import { getTargetClassName, twMerge } from '@panwds/tailwindcss';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Tooltip } from '../../Tooltip/index.js';
import { TruncateText } from '../../TruncateText/index.js';
import { ADVANCED_VERTICAL_STEPPER_STATUS, ADVANCED_VERTICAL_STEPPER_TYPE } from '../types.js';
import { AdvancedVerticalStepperContext } from './advanced_vertical_stepper.js';
import { findPreviousFilledStatus, getConnectorBackgroundColor, getConnectorBorderColor, getContentBackgroundColor, getContentBorderColor, getContentIconBackgroundColor, getContentIconTextColor } from './advanced_vertical_stepper.logic.js';
import { AdvancedVerticalStepperConnector } from './advanced_vertical_stepper_connector.js';
const getDefaultStatusIcon = (status) => {
    if (status === ADVANCED_VERTICAL_STEPPER_STATUS.COMPLETE) {
        return React.createElement(CheckIcon, { size: "xxs" });
    }
    if (status === ADVANCED_VERTICAL_STEPPER_STATUS.ERROR) {
        return React.createElement(ExclamationMarkIcon, { size: "xxs" });
    }
    return null;
};
export const AdvancedVerticalStepperBody = (props) => {
    const { position, currentPosition, className, click } = props;
    const { items, stepperType } = useContext(AdvancedVerticalStepperContext);
    const { label, isNextUngroupedItem, description, getContentIcon, getStatusIcon, status, headerLabel, optional } = items[position] ?? {};
    const inner = useRef(null);
    const outer = useRef(null);
    const [tooltip, setTooltip] = useState(false);
    const [innerHeight, setInnerHeight] = useState(0);
    const [outerHeight, setOuterHeight] = useState(0);
    const isPreviousNonOptionalItemFilled = findPreviousFilledStatus(items, position);
    useEffect(() => {
        if (!innerHeight || !outerHeight) {
            return;
        }
        if (innerHeight > outerHeight) {
            setTooltip(true);
        }
        else {
            setTooltip(false);
        }
    }, [innerHeight, outerHeight]);
    useEffect(() => {
        if (inner.current?.getBoundingClientRect().height) {
            setInnerHeight(inner.current?.getBoundingClientRect().height);
        }
        if (outer.current?.getBoundingClientRect().height) {
            setOuterHeight(outer.current.getBoundingClientRect().height);
        }
    }, [inner.current?.getBoundingClientRect().height, outer.current?.getBoundingClientRect().height]);
    const content = (React.createElement("div", { ref: outer, className: "twds-max-h-[3rem] twds-line-clamp-3 twds-text-2xs twds-leading-4" },
        React.createElement("span", { ref: inner, className: twMerge('twds-text-wrap twds-text-hint dark:twds-text-hint-dark twds-font-normal', !(status || currentPosition === position || items[position - 1]?.status || optional || position === 0) &&
                stepperType === ADVANCED_VERTICAL_STEPPER_TYPE.CONSECUTIVE &&
                'twds-text-disabled dark:twds-text-disabled-dark', getTargetClassName(className, 'description', { currentPosition, position })) }, description)));
    return (React.createElement("div", { className: "twds-flex" },
        stepperType === 'consecutive' && (React.createElement(AdvancedVerticalStepperConnector, { position: position, currentPosition: currentPosition, className: className?.advancedVerticalStepperConnector, onClick: click?.onStatusIconClick, isPreviousNonOptionalItemFilled: isPreviousNonOptionalItemFilled })),
        React.createElement("div", { className: twMerge(isNextUngroupedItem ? 'twds-pb-8' : 'twds-pb-2') },
            headerLabel && (React.createElement("div", { className: twMerge('twds-flex twds-text-xs twds-pt-6 twds-pb-2 twds-w-full twds-uppercase twds-text-primary dark:twds-text-primary-dark', getTargetClassName(className, 'headerLabel', { currentPosition, position })) },
                React.createElement(TruncateText, null, headerLabel))),
            React.createElement("div", { className: twMerge('twds-flex twds-justify-between', 'twds-max-w-[15.25rem] twds-w-[15.25rem]', 'twds-px-4 twds-py-3', 'twds-border-solid twds-border twds-border-gray-300 dark:twds-border-gray-900 twds-rounded', (stepperType === ADVANCED_VERTICAL_STEPPER_TYPE.NON_CONSECUTIVE ||
                    position === 0 ||
                    currentPosition === position ||
                    isPreviousNonOptionalItemFilled ||
                    optional) &&
                    'hover:twds-cursor-pointer', getContentBackgroundColor({
                    status,
                    currentPosition,
                    position,
                    isPreviousNonOptionalItemFilled,
                    optional,
                    stepperType
                }), getContentBorderColor({
                    status,
                    currentPosition,
                    position,
                    isPreviousNonOptionalItemFilled,
                    optional,
                    stepperType
                }), getTargetClassName(className, 'root', { currentPosition, position })), onClick: () => click?.onCardClick?.({
                    currentPosition,
                    position,
                    isPreviousNonOptionalItemFilled
                }) },
                React.createElement("div", { className: "twds-flex twds-gap-2 twds-max-w-full" },
                    React.createElement("div", { className: twMerge('twds-flex twds-min-w-8 twds-min-h-8 twds-max-w-8 twds-max-h-8 twds-justify-center twds-items-center', 'twds-rounded-[4px]', getContentIconBackgroundColor({ status, currentPosition, position }), getContentIconTextColor({
                            status,
                            currentPosition,
                            position,
                            isPreviousNonOptionalItemFilled,
                            optional,
                            stepperType
                        }), getTargetClassName(className, 'contentIcon', { currentPosition, position })) }, getContentIcon?.({ status, currentPosition, position })),
                    React.createElement("div", { className: "twds-flex twds-flex-col twds-max-w-[85%]" },
                        stepperType === 'consecutive' && (React.createElement(TruncateText, { addClassName: "twds-block twds-text-2xs twds-leading-4" },
                            React.createElement("span", { className: twMerge('twds-text-secondary dark:twds-text-secondary-dark twds-text-2xs twds-leading-4 twds-font-normal', !(status ||
                                    currentPosition === position ||
                                    isPreviousNonOptionalItemFilled ||
                                    optional ||
                                    position === 0) &&
                                    stepperType === ADVANCED_VERTICAL_STEPPER_TYPE.CONSECUTIVE &&
                                    'twds-text-disabled dark:twds-text-disabled-dark', getTargetClassName(className, 'step', { currentPosition, position })) },
                                "Step ",
                                position + 1))),
                        React.createElement("div", { className: "twds-flex twds-gap-1" },
                            React.createElement(TruncateText, { addClassName: "twds-block twds-text-xs twds-leading-4" },
                                React.createElement("span", { className: twMerge('twds-text-primary dark:twds-text-primary-dark twds-text-xs twds-font-bold', !(status ||
                                        currentPosition === position ||
                                        isPreviousNonOptionalItemFilled ||
                                        optional ||
                                        position === 0) &&
                                        stepperType === ADVANCED_VERTICAL_STEPPER_TYPE.CONSECUTIVE &&
                                        'twds-text-disabled dark:twds-text-disabled-dark', getTargetClassName(className, 'label', { currentPosition, position })) }, label)),
                            optional && (React.createElement("div", { className: "twds-text-xs twds-leading-4 twds-text-secondary dark:twds-text-secondary-dark" }, "(Optional)"))),
                        tooltip ? React.createElement(Tooltip, { label: description }, content) : content)),
                stepperType === ADVANCED_VERTICAL_STEPPER_TYPE.NON_CONSECUTIVE && currentPosition !== position && status && (React.createElement("div", { className: "twds-flex twds-justify-center", onClick: (e) => {
                        e.stopPropagation();
                        click?.onStatusIconClick?.({
                            currentPosition,
                            position,
                            isPreviousNonOptionalItemFilled
                        });
                    } },
                    React.createElement("div", { className: twMerge('twds-flex twds-justify-center twds-items-center', !status
                            ? 'twds-min-w-[12px] twds-min-h-[12px] twds-max-w-[12px] twds-max-h-[12px]'
                            : 'twds-min-w-[16px] twds-min-h-[16px] twds-max-w-[16px] twds-max-h-[16px]', 'twds-rounded-full twds-text-primary-dark', !status && 'twds-border-solid twds-border-2', getConnectorBackgroundColor({ status, currentPosition, position }), getConnectorBorderColor({ status, currentPosition, position }), getTargetClassName(className, 'icon', { currentPosition, position })) }, (currentPosition !== position && status && getStatusIcon?.({ currentPosition, position, status })) ??
                        getDefaultStatusIcon(status))))))));
};
//# sourceMappingURL=advanced_vertical_stepper_body.js.map